/**
 * Interceptor global de window.fetch para inyectar el header
 * X-Impersonation-Token en todas las requests al backend mientras
 * haya una sesión de impersonation activa.
 *
 * Diseño:
 * - Se instala UNA VEZ en index.js, antes de renderizar.
 * - Lee el token de sessionStorage.IMP_TOKEN.
 * - Si la respuesta es 401/403 con detail.code IMPERSONATION_*,
 *   limpia el token y dispara el evento "impersonation-cleared"
 *   para que la UI vuelva al modo regular.
 * - Solo intercepta requests al mismo origen (Same-Origin) o que
 *   apunten a /api/... — para no afectar a Firebase, Google APIs, etc.
 */

const STORAGE_TOKEN = "IMP_TOKEN";
const STORAGE_META = "IMP_META";
const EVT_CHANGED = "impersonation-changed";
const EVT_CLEARED = "impersonation-cleared";
const EVT_ERROR = "impersonation-error";

export function getImpersonationToken() {
  try {
    return sessionStorage.getItem(STORAGE_TOKEN) || null;
  } catch (_) {
    return null;
  }
}

export function getImpersonationMeta() {
  try {
    const raw = sessionStorage.getItem(STORAGE_META);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function setImpersonation(token, meta) {
  if (token) {
    sessionStorage.setItem(STORAGE_TOKEN, token);
    if (meta) sessionStorage.setItem(STORAGE_META, JSON.stringify(meta));
  } else {
    sessionStorage.removeItem(STORAGE_TOKEN);
    sessionStorage.removeItem(STORAGE_META);
  }
  // Notifica a esta tab y al resto vía BroadcastChannel
  window.dispatchEvent(new CustomEvent(EVT_CHANGED, { detail: { token, meta } }));
  try {
    if (window.__impBC) window.__impBC.postMessage({ type: "changed", token, meta });
  } catch (_) {}
}

export function clearImpersonation(reason = "manual") {
  setImpersonation(null, null);
  window.dispatchEvent(new CustomEvent(EVT_CLEARED, { detail: { reason } }));
}

let _installed = false;

/**
 * Instala el interceptor en window.fetch. Idempotente.
 */
export function installImpersonationFetch() {
  if (_installed) return;
  _installed = true;

  // Sincronización entre tabs vía BroadcastChannel (si está disponible)
  try {
    const bc = new BroadcastChannel("nara-impersonation");
    window.__impBC = bc;
    bc.onmessage = (e) => {
      if (!e?.data) return;
      // Re-emitir el cambio en esta tab para que la UI se actualice
      window.dispatchEvent(new CustomEvent(EVT_CHANGED, { detail: e.data }));
    };
  } catch (_) {}

  const realFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const token = getImpersonationToken();
    if (!token) return realFetch(input, init);

    // Solo inyecta el header en requests al backend (proxy /api o mismo origen).
    // Si es una URL absoluta a otro dominio (Firebase, Google), respetar.
    const url = typeof input === "string" ? input : (input?.url || "");
    let isOurApi = true;
    try {
      if (url.startsWith("http://") || url.startsWith("https://")) {
        const u = new URL(url);
        isOurApi = u.origin === window.location.origin;
      }
      // url relativa o /api/... → asumimos backend
    } catch (_) {
      isOurApi = true;
    }
    if (!isOurApi) return realFetch(input, init);

    const headers = new Headers(init.headers || (typeof input !== "string" ? input.headers : undefined));
    headers.set("X-Impersonation-Token", token);

    const response = await realFetch(input, { ...init, headers });

    if (response.status === 401 || response.status === 403) {
      // Lee el body sin consumirlo (clone)
      try {
        const data = await response.clone().json();
        const code = data?.detail?.code || "";
        if (typeof code === "string" && code.startsWith("IMPERSONATION_")) {
          const message = data?.detail?.message || code;
          clearImpersonation(code);
          window.dispatchEvent(
            new CustomEvent(EVT_ERROR, { detail: { code, message } }),
          );
        }
      } catch (_) {}
    }

    return response;
  };
}

export const IMP_EVENTS = {
  CHANGED: EVT_CHANGED,
  CLEARED: EVT_CLEARED,
  ERROR: EVT_ERROR,
};
